import * as React from 'react';
import { ChevronUp } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export interface NumberInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  /** Classes for the <input> itself; `className` styles the stepper box. */
  inputClassName?: string | undefined;
}

// React tracks an input's value through its own setter; setting it with the native one
// and firing "input" makes React (and react-hook-form's register) see a real change.
function setNativeValue(el: HTMLInputElement, value: string): void {
  Reflect.set(HTMLInputElement.prototype, 'value', value, el);
}

function decimalsOf(text: string | number | readonly string[] | undefined): number {
  const [, fraction = ''] = String(text ?? '').split('.');
  return fraction.length;
}

const BUTTON_CLASS =
  'flex h-[14px] w-[18px] items-center justify-center rounded bg-surface-high text-muted-foreground transition-colors hover:bg-primary/15 hover:text-primary disabled:pointer-events-none disabled:opacity-50';

/**
 * A number field styled like the filter steppers (NumberStepper): the native spinner is
 * hidden and two small ▲/▼ buttons step the value. Unlike NumberStepper it is a plain
 * <input type="number"> underneath, so it takes decimals (step 0.1, "any"), min/max and
 * works with both controlled inputs and react-hook-form's register().
 */
const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ className, inputClassName, disabled, id, onWheel, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    // step="any" has no step size to bump by: keep the look, drop the buttons.
    const steppable = props.step !== 'any';

    const setRefs = React.useCallback(
      (el: HTMLInputElement | null) => {
        inputRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      },
      [ref],
    );

    function bump(direction: 1 | -1) {
      const el = inputRef.current;
      if (!el || disabled) return;
      const before = el.value;
      // An empty field steps from min (or 0), like typing the first digit.
      if (before === '') setNativeValue(el, el.min !== '' ? el.min : '0');
      try {
        if (direction === 1) el.stepUp();
        else el.stepDown();
      } catch {
        setNativeValue(el, before);
        return;
      }
      if (el.value === before) return;
      // stepUp can leave float noise (0.2 + 0.1): round to the finest decimals involved.
      const digits = Math.max(decimalsOf(props.step), decimalsOf(el.min), decimalsOf(before));
      setNativeValue(el, String(Number(Number(el.value).toFixed(digits))));
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }

    return (
      <div
        className={cn(
          'flex h-10 items-center rounded-lg bg-surface-container pl-3 pr-1 focus-within:ring-1 focus-within:ring-primary',
          disabled && 'opacity-50',
          className,
        )}
      >
        <input
          ref={setRefs}
          type="number"
          id={inputId}
          disabled={disabled}
          {...props}
          // A wheel turn over a focused number field would change its value: drop focus instead.
          onWheel={(e) => {
            e.currentTarget.blur();
            onWheel?.(e);
          }}
          className={cn(
            'h-full min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            inputClassName,
          )}
        />
        {steppable && (
          <span className="flex shrink-0 flex-col gap-0.5 py-1">
            <button
              type="button"
              tabIndex={-1}
              disabled={disabled}
              aria-label="Увеличить"
              aria-controls={inputId}
              onClick={() => bump(1)}
              className={BUTTON_CLASS}
            >
              <ChevronUp className="h-[11px] w-[11px]" />
            </button>
            <button
              type="button"
              tabIndex={-1}
              disabled={disabled}
              aria-label="Уменьшить"
              aria-controls={inputId}
              onClick={() => bump(-1)}
              className={BUTTON_CLASS}
            >
              <ChevronUp className="h-[11px] w-[11px] rotate-180" />
            </button>
          </span>
        )}
      </div>
    );
  },
);
NumberInput.displayName = 'NumberInput';

export { NumberInput };
